<template>
  <div class="ksk-qwerty">
    <div v-for="(row, r) in rows" :key="r" class="ksk-qwerty__row">
      <button
        v-for="key in row"
        :key="key"
        type="button"
        class="ksk-qwerty__key"
        :class="keyClass(key)"
        :aria-label="labelFor(key)"
        @click="press(key)"
      >
        <span v-if="key === '{bksp}'" class="material-icons-round">backspace</span>
        <span v-else-if="key === '{shift}'" class="material-icons-round">
          {{ shift ? "keyboard_capslock" : "arrow_upward" }}
        </span>
        <span v-else-if="key === '{space}'">{{ $t("kiosk.keyboard.space") }}</span>
        <span v-else-if="key === '{clear}'">{{ $t("kiosk.keyboard.clear") }}</span>
        <span v-else>{{ shift ? key.toUpperCase() : key }}</span>
      </button>
    </div>
  </div>
</template>

<script>
// KSK005 — full-width QWERTY on-screen keyboard, 80px keys, with a number row,
// Shift (one-shot), Backspace, Space and Clear. v-model is the text value.
export default {
  name: "KskQwertyKeyboard",
  emits: ["update:modelValue", "press"],
  props: {
    modelValue: { type: String, default: "" },
  },
  data() {
    return {
      shift: false,
      rows: [
        ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
        ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
        ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
        ["{shift}", "z", "x", "c", "v", "b", "n", "m", "{bksp}"],
        ["{clear}", "{space}"],
      ],
    };
  },
  methods: {
    press(key) {
      const value = this.modelValue || "";
      let next = value;
      if (key === "{shift}") {
        this.shift = !this.shift;
      } else if (key === "{bksp}") {
        next = value.slice(0, -1);
      } else if (key === "{clear}") {
        next = "";
      } else if (key === "{space}") {
        next = value + " ";
      } else {
        next = value + (this.shift ? key.toUpperCase() : key);
        this.shift = false;
      }
      this.$emit("press", key);
      if (next !== value) this.$emit("update:modelValue", next);
    },
    keyClass(key) {
      return {
        "ksk-qwerty__key--action": key.startsWith("{"),
        "ksk-qwerty__key--wide": key === "{shift}" || key === "{bksp}" || key === "{clear}",
        "ksk-qwerty__key--space": key === "{space}",
        "ksk-qwerty__key--on": key === "{shift}" && this.shift,
      };
    },
    labelFor(key) {
      const keys = { "{bksp}": "backspace", "{shift}": "shift", "{space}": "space", "{clear}": "clear" };
      return keys[key] ? this.$t("kiosk.keyboard." + keys[key]) : key;
    },
  },
};
</script>

<style scoped>
.ksk-qwerty {
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-1);
  width: 100%;
}
.ksk-qwerty__row {
  display: flex;
  justify-content: center;
  gap: var(--ksk-space-1);
}
.ksk-qwerty__key {
  position: relative;
  flex: 1 1 0;
  max-width: calc((100% - 9 * var(--ksk-space-1)) / 10);
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* KSK063: muted key tiles, no outline (same as the numeric keypad). */
  border: 1px solid transparent;
  border-radius: 16px;
  background: var(--ksk-surface-muted);
  color: var(--ksk-text);
  font-family: var(--ksk-font);
  font-size: 30px;
  line-height: 36px;
  font-weight: 500;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  transition: transform 80ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
.ksk-qwerty__key--action {
  background: var(--ksk-surface-muted);
  border-color: transparent;
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
}
.ksk-qwerty__key--wide {
  flex-grow: 1.5;
  max-width: none;
}
.ksk-qwerty__key--space {
  flex-grow: 6;
  max-width: none;
}
.ksk-qwerty__key--on {
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
}
.ksk-qwerty__key .material-icons-round {
  font-size: 32px;
}
.ksk-qwerty__key::after {
  content: "";
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
}
.ksk-qwerty__key:active {
  transform: scale(0.97);
}
.ksk-qwerty__key:active::after {
  opacity: 0.08;
}
</style>
